import { CHANNEL_HEADER, getChannelCode, request, resolveApiUrl, tokenManager } from '../../shared/api/request'
import type { ChatBootstrap, ChatJob, ChatResponse, ChatShare, Conversation, RenderAction } from './chatTypes'

export type ChatApi = {
  bootstrap: () => Promise<ChatBootstrap>
  submitJob: (input: { conversationId?: string | null; contextId?: string | null; content: string }) => Promise<ChatJob>
  getJob: (jobId: string) => Promise<ChatJob>
  streamJob: (jobId: string, onProgress: (response: ChatResponse) => void) => Promise<ChatResponse>
  listConversations: () => Promise<Conversation[]>
  loadConversation: (id: string) => Promise<ChatResponse>
  deleteConversation: (id: string) => Promise<void>
  renameConversation: (id: string, title: string) => Promise<Conversation>
  pinConversation: (id: string, pinned: boolean) => Promise<Conversation>
  createShare: (id: string, messageIds?: string[]) => Promise<ChatShare>
  executeAction: (action: RenderAction, contextId: string, conversationId: string | null) => Promise<ChatResponse>
}

export const chatApi: ChatApi = {
  bootstrap: () => request({ url: '/api/mini/chat/bootstrap' }),
  submitJob: ({ conversationId, contextId, content }) => request({ url: '/api/mini/chat/messages/jobs', method: 'POST', data: { conversationId, contextId, clientMessageId: crypto.randomUUID(), content } }),
  getJob: (jobId) => request({ url: `/api/mini/chat/messages/jobs/${encodeURIComponent(jobId)}` }),
  streamJob: streamChatJob,
  listConversations: () => request({ url: '/api/mini/chat/conversations' }),
  loadConversation: (id) => request({ url: `/api/mini/chat/conversations/${encodeURIComponent(id)}/messages` }),
  deleteConversation: (id) => request({ url: `/api/mini/chat/conversations/${encodeURIComponent(id)}`, method: 'DELETE' }),
  renameConversation: (id, title) => request({ url: `/api/mini/chat/conversations/${encodeURIComponent(id)}`, method: 'PUT', data: { title } }),
  pinConversation: (id, pinned) => request({ url: `/api/mini/chat/conversations/${encodeURIComponent(id)}/pin`, method: 'PUT', data: { pinned } }),
  createShare: (id, messageIds) => request({ url: `/api/mini/chat/conversations/${encodeURIComponent(id)}/share`, method: 'POST', data: messageIds?.length ? { messageIds } : undefined }),
  executeAction: (action, contextId, conversationId) => request({ url: `/api/mini/chat/actions/${encodeURIComponent(String(action.payload?.actionId || ''))}/execute`, method: 'POST', data: { contextId, conversationId, confirmed: true }, timeout: 120000 })
}

export function getConversationShare(token: string) { return request<ChatShare>({ url: `/api/mini/chat/shares/${encodeURIComponent(token)}`, auth: false }) }

export async function streamChatJob(jobId: string, onProgress: (response: ChatResponse) => void): Promise<ChatResponse> {
  const accessToken = tokenManager.getAccessToken()
  const response = await fetch(resolveApiUrl(`/api/mini/chat/messages/jobs/${encodeURIComponent(jobId)}/stream`), {
    headers: { Accept: 'text/event-stream', [CHANNEL_HEADER]: getChannelCode(), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) }
  })
  if (!response.ok || !response.body) throw new Error(`stream request failed: ${response.status}`)
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  while (true) {
    const { value, done } = await reader.read()
    buffer += decoder.decode(value, { stream: !done })
    const frames = buffer.split(/\r?\n\r?\n/)
    buffer = frames.pop() || ''
    for (const frame of frames) {
      const data = frame.split(/\r?\n/).filter((line) => line.startsWith('data:')).map((line) => line.slice(5).trimStart()).join('\n')
      if (!data) continue
      const job = JSON.parse(data) as ChatJob
      if (job.status === 'FAILED') { await reader.cancel(); throw new Error(job.errorMessage || '消息处理失败') }
      if (job.status === 'COMPLETED' && job.result) { await reader.cancel(); return job.result }
      if (job.result) onProgress(job.result)
    }
    if (done) break
  }
  throw new Error('流式连接已中断')
}

export async function pollChatJob(api: ChatApi, jobId: string, intervalMs = 500, maxAttempts = 600, onProgress?: (response: ChatResponse) => void): Promise<ChatResponse> {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const job = await api.getJob(jobId)
    if (job.status === 'COMPLETED' && job.result) return job.result
    if (job.status === 'FAILED') throw new Error(job.errorMessage || '消息处理失败')
    if (job.result) onProgress?.(job.result)
    if (attempt < maxAttempts - 1) await new Promise((resolve) => window.setTimeout(resolve, intervalMs))
  }
  throw new Error('消息仍在处理中，可稍后刷新查看')
}
